import type { ReactElement, ReactNode } from 'react';
import { List } from 'lucide-react';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { PRESSED_ACTION_BUTTON_CLASS } from '@/components/alertActionStyles';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AlertMuteButton } from '@/components/AlertMuteButton';
import { AlertUnmuteButton } from '@/components/AlertUnmuteButton';
import { AlertExcludeButton } from '@/components/AlertExcludeButton';
import { AlertRemoveExclusionButton } from '@/components/AlertRemoveExclusionButton';
import { AlertEditPolicyButton } from '@/components/AlertEditPolicyButton';
import type { AlertFactsStripVariant } from './AlertFactsStrip';
import { SessionTimelineButton } from './session-timeline/SessionTimelineButton';
import { SessionViewButton } from './session-view/SessionViewButton';

/** Hover hint on "Show Instances". */
const SHOW_INSTANCES_HINT =
  'See every time this alert fired, each with its own user, endpoint and details, and any other paths where the same content was found.';

/** "1 instance", "2 instances". */
function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

interface AlertActionsBarProps {
  alert: GroupedAlert;
  /** Present for the org surface (enables the org-scoped Edit Policy action). */
  organizationId?: string;
  /**
   * KIR-4281 — `'occurrence'` keeps only the session timeline: Mute, Exclude
   * and Edit Policy are keyed on `alertKey` and would act on the whole group.
   */
  variant?: AlertFactsStripVariant;
  /**
   * Omitted → no "Show Instances" button (e.g. already on the occurrences
   * page). Also hidden for a single instance with no other paths (KIR-5368):
   * the panel already shows everything the dialog would.
   */
  onShowOccurrences?: () => void;
  /** Rendered last; the caller pushes it to the far end (Add to Allow/Block List). */
  endSlot?: ReactNode;
}

/**
 * KIR-4481 — the alert actions at the top of the expanded panel: Mute/Unmute,
 * Exclude/Remove, Edit Policy, Session Timeline, View all alerts in this
 * session (KIR-4785) and Show Instances (with the
 * group's instance count under it), in that
 * fixed order and left-aligned so none shifts when another (or the end slot)
 * is absent. Reuses the exact self-contained buttons the Actions column
 * renders — each owns its own mutation, confirm dialog, cache invalidation and
 * role gate (`useOrgWriteGate`), so a member sees only the read-only ones.
 * `empty:hidden` drops the row when every button hides itself.
 */
export function AlertActionsBar({
  alert,
  organizationId,
  variant = 'group',
  onShowOccurrences,
  endSlot,
}: AlertActionsBarProps): ReactElement {
  const scope = organizationId
    ? ({ kind: 'organization', organizationId } as const)
    : ({ kind: 'account' } as const);
  const analyticsContext = { severity: alert.severity, type: alert.type };

  return (
    // Stop clicks so an action never toggles the row. Not pointerdown: React
    // bubbles it out of the portaled dialogs below, and stopping it here hides
    // the outside press Radix needs to close them.
    <div
      data-testid="alert-actions-bar"
      className="flex flex-wrap items-center gap-2 empty:hidden"
      onClick={event => event.stopPropagation()}
    >
      {variant !== 'occurrence' && (
        <>
          {alert.mute?.muted ? (
            <AlertUnmuteButton
              scope={scope}
              alertKey={alert.alertKey}
              mute={alert.mute}
              analyticsContext={analyticsContext}
            />
          ) : (
            <AlertMuteButton
              scope={scope}
              alertKey={alert.alertKey}
              analyticsContext={analyticsContext}
            />
          )}
          {alert.exclude?.excluded ? (
            <AlertRemoveExclusionButton
              scope={scope}
              alertKey={alert.alertKey}
              exclude={alert.exclude}
              analyticsContext={analyticsContext}
            />
          ) : (
            <AlertExcludeButton
              scope={scope}
              alertKey={alert.alertKey}
              analyticsContext={analyticsContext}
              findingType={alert.type}
            />
          )}
          {/* Rendered unconditionally (not gated on the browser flag) for both
              scopes: AlertEditPolicyButton self-hides for a member /
              self-disables for an org with the flag off, and the account
              variant edits the user's own policy. Gating here could omit it
              while the Actions column still shows it (Bugbot). */}
          <AlertEditPolicyButton
            scope={scope}
            policyKey={alert.policyKey}
            subPolicyKey={alert.subPolicyKey}
            alertKey={alert.alertKey}
          />
        </>
      )}
      <SessionTimelineButton
        link={alert.sessionLink}
        organizationId={organizationId}
      />
      <SessionViewButton
        link={alert.sessionLink}
        organizationId={organizationId}
      />
      {onShowOccurrences &&
        (alert.occurrenceCount !== 1 || alert.otherPathCount !== undefined) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={PRESSED_ACTION_BUTTON_CLASS}
                onClick={onShowOccurrences}
              >
                <List className="size-4" />
                {/* Same two tight lines as Session Timeline. Other paths are
                  places, not instances, so they get their own count. */}
                <span className="flex flex-col items-start gap-0.5 leading-none">
                  <span>Show Instances</span>
                  {alert.occurrenceCount !== undefined && (
                    <span className="text-[11px] font-normal text-muted-foreground">
                      {countLabel(alert.occurrenceCount, 'instance')}
                      {alert.otherPathCount !== undefined &&
                        ` · ${countLabel(alert.otherPathCount, 'other path')}`}
                    </span>
                  )}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {SHOW_INSTANCES_HINT}
            </TooltipContent>
          </Tooltip>
        )}
      {endSlot}
    </div>
  );
}

AlertActionsBar.displayName = 'AlertActionsBar';
